import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import api from '../api/api';
import { reportPreferenceRecovery, reportPreferenceRecoveryMessage } from '../utils/reportPreferenceRecovery';
import { extractApiErrorMessage } from '../utils/apiError';
import { restaurantReportDefaultFields } from '../utils/restaurantReportFields';
import {
  acknowledgeUnknownPersonalReportDelivery, beginPersonalReportDelivery, finishPersonalReportDelivery,
  usePersonalReportDeliveryActivity,
} from '../utils/personalReportDeliveryActivity';
import './PersonalReportDeliveryPanel.css';

type Channel = 'telegram' | 'viber';
type Template = 'employee-statement' | 'employee-timesheet' | 'monthly-timesheet';
type Field = 'position' | 'attendance' | 'attendance-intervals' | 'attendance-hours' |
  'attendance-coefficients' | 'attendance-notes' | 'payroll' | 'payroll-breakdown' |
  'payroll-method' | 'payroll-rates' | 'payroll-revenue' | 'payroll-bonuses' |
  'payroll-penalties' | 'adjustments' |
  'department' | 'daily-hours' | 'weighted-hours' | 'interval-count' | 'notes';
interface Preferences { fields: Field[]; version: number; channel: Channel }
interface LinkState { channel: Channel; consentedAtUtc?: string }
interface LinkResponse {
  links: LinkState[];
  providers: { telegramReady: boolean; viberReady: boolean };
  attendanceDeliveryAllowed?: boolean;
  revenueDeliveryAllowed?: boolean;
}
interface Preview {
  previewId: string;
  channel: Channel;
  text: string;
  deliveryFormat: 'text' | 'private-pdf';
  expiresAtUtc: string;
  contextKey: string;
  generation: number;
}
type PreviewResponse = Omit<Preview, 'contextKey' | 'generation'>;

const statementFields: Array<[Field, string]> = [
  ['position', 'Посада'],
  ['attendance', 'Дані табеля'],
  ['attendance-intervals', 'Інтервали приходу й виходу'],
  ['attendance-hours', 'Години табеля'],
  ['attendance-coefficients', 'Коефіцієнти табеля'],
  ['attendance-notes', 'Примітки табеля'],
  ['payroll', 'Дані зарплати'],
  ['payroll-breakdown', 'Базове нарахування'],
  ['payroll-method', 'Метод нарахування'],
  ['payroll-rates', 'Особисті ставки та частка виторгу'],
  ['payroll-revenue', 'Виторг ресторану у знімку'],
  ['payroll-bonuses', 'Премії'],
  ['payroll-penalties', 'Штрафи й утримання'],
  ['adjustments', 'Окремі коригування'],
];
const personalTimesheetFields: Array<[Field, string]> = [
  ['position', 'Посада'],
  ['attendance', 'Дані табеля'],
  ['attendance-intervals', 'Інтервали приходу й виходу'],
  ['attendance-hours', 'Години табеля'],
  ['attendance-coefficients', 'Коефіцієнти табеля'],
  ['attendance-notes', 'Примітки табеля'],
];
const monthlyTimesheetFields: Array<[Field, string]> = [
  ['position', 'Посада'],
  ['department', 'Підрозділ'],
  ['daily-hours', 'Фактичні години за днями'],
  ['weighted-hours', 'Зараховані години за днями'],
  ['interval-count', 'Кількість інтервалів'],
  ['notes', 'Примітки з інтервалів'],
];

interface Props {
  template: Template;
  year: number;
  month: number;
  employmentId?: number;
  scopeKey?: string;
  reportBlocked?: boolean;
}

const PersonalReportDeliveryPanel: React.FC<Props> = ({
  template, year, month, employmentId, scopeKey, reportBlocked = false,
}) => {
  const [linkState, setLinkState] = useState<{ data: LinkResponse; contextKey: string } | null>(null);
  const [channel, setChannel] = useState<Channel>('telegram');
  const [consentState, setConsentState] = useState<{ value: boolean; contextKey: string } | null>(null);
  const [pairing, setPairing] = useState<{ code: string; contextKey: string } | null>(null);
  const [preferences, setPreferences] = useState<Preferences>({
    fields: [...restaurantReportDefaultFields(template, 'telegram')] as Field[],
    version: 0, channel: 'telegram',
  });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [statusState, setStatusState] = useState<{ text: string; contextKey: string } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [savingFields, setSavingFields] = useState(false);
  const request = React.useRef<AbortController | null>(null);
  const generation = React.useRef(0);
  const saveInProgress = React.useRef(false);
  const loadedPreferencesKey = useRef('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const contextGeneration = useRef(0);
  const activity = usePersonalReportDeliveryActivity();
  const focusAfterConfirm = useRef(false);
  const statusRef = useRef<HTMLParagraphElement | null>(null);
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  const contextKey = JSON.stringify({
    scopeKey,
    employmentId: employmentId ?? null,
    template,
    year,
    month,
    channel,
    reportBlocked,
    fields: preferences.fields,
  });
  const loadContextKey = JSON.stringify([
    scopeKey, employmentId ?? null, template, year, month, channel,
  ]);
  const identityContextKey = JSON.stringify([scopeKey, employmentId ?? null, channel]);
  const consent = consentState?.contextKey === identityContextKey && consentState.value;
  const setConsent = (value: boolean) => setConsentState({ value, contextKey: identityContextKey });
  const preferenceKey = JSON.stringify([template, channel]);
  const status = statusState?.contextKey === loadContextKey ? statusState.text : '';
  const setStatus = (text: string) => setStatusState(text ? { text, contextKey: loadContextKey } : null);
  const pairingCode = pairing?.contextKey === identityContextKey ? pairing.code : '';
  const setPairingCode = (code: string) => setPairing(code ? { code, contextKey: identityContextKey } : null);
  const activeLinks = linkState?.contextKey === loadContextKey ? linkState.data : null;
  const links = activeLinks?.links ?? [];
  const providers = activeLinks?.providers ?? { telegramReady: false, viberReady: false };
  const attendanceDeliveryAllowed = employmentId == null || activeLinks?.attendanceDeliveryAllowed === true;
  const revenueDeliveryAllowed = activeLinks?.revenueDeliveryAllowed === true;
  const currentContextKey = React.useRef(contextKey);
  const currentLoadContextKey = React.useRef(loadContextKey);
  const currentIdentityContextKey = React.useRef(identityContextKey);
  currentContextKey.current = contextKey;
  currentLoadContextKey.current = loadContextKey;
  currentIdentityContextKey.current = identityContextKey;

  const load = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const currentGeneration = ++generation.current;
    const requestContextKey = loadContextKey;
    setBusy(true);
    setError('');
    try {
      const linkResponse = await api.get<LinkResponse>(employmentId == null
        ? '/personal-report-delivery/links'
        : `/personal-report-delivery/employees/${employmentId}/link-status`, { signal: controller.signal });
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentLoadContextKey.current !== requestContextKey) return;
      const linkData = linkResponse.data;
      if (!Array.isArray(linkData?.links) ||
          typeof linkData.providers?.telegramReady !== 'boolean' ||
          typeof linkData.providers?.viberReady !== 'boolean')
        throw new Error('Стан налаштувань приватної відправки недоступний.');
      setLinkState({ data: linkData, contextKey: requestContextKey });
      if (loadedPreferencesKey.current === preferenceKey) return;
      const query = `?channel=${channel}`;
      const preferencesUrl = template === 'employee-statement' ||
        (employmentId == null && template !== 'monthly-timesheet')
        ? `/personal-report-delivery/me/fields/${template}${query}`
        : `/restaurant-personnel/report-preferences/${template}${query}`;
      const fieldsResponse = await api.get<Preferences>(preferencesUrl, { signal: controller.signal });
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentLoadContextKey.current !== requestContextKey) return;
      if (!Array.isArray(fieldsResponse.data?.fields) || typeof fieldsResponse.data.version !== 'number' ||
          fieldsResponse.data.channel !== channel)
        throw new Error('Збережені поля звіту недоступні.');
      setPreferences(fieldsResponse.data);
      setRecoveryKey('');
      loadedPreferencesKey.current = preferenceKey;
    } catch (failure) {
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentLoadContextKey.current === requestContextKey) {
        const recovery = reportPreferenceRecovery(failure, template, channel);
        if (recovery) {
          setPreferences({ fields: [], channel, version: recovery.version });
          loadedPreferencesKey.current = preferenceKey;
          setRecoveryKey(preferenceKey);
          setPreview(null);
          return;
        }
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити налаштування приватної відправки.'));
      }
    } finally {
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentLoadContextKey.current === requestContextKey) setBusy(false);
    }
  }, [employmentId, template, channel, loadContextKey, preferenceKey]);

  const invalidate = useCallback(() => {
    focusAfterConfirm.current = false;
    generation.current++;
    if (!saveInProgress.current) {
      request.current?.abort();
      request.current = null;
    }
    setPreview(null);
  }, []);

  useEffect(() => {
    if (!busy && focusAfterConfirm.current) {
      (error ? errorRef.current : statusRef.current)?.focus();
      focusAfterConfirm.current = false;
    }
  }, [busy, error, status]);

  const previousIdentityContextKey = React.useRef(identityContextKey);
  useLayoutEffect(() => {
    if (previousIdentityContextKey.current === identityContextKey) return;
    previousIdentityContextKey.current = identityContextKey;
    setConsentState(null);
    setPairing(null);
    setStatusState(null);
    setError('');
  }, [identityContextKey]);

  useLayoutEffect(() => {
    ++contextGeneration.current;
    invalidate();
    setLinkState(null);
    setStatusState(null);
    void load();
    return () => {
      contextGeneration.current += 1;
      generation.current += 1;
      request.current?.abort();
      request.current = null;
    };
  }, [invalidate, load, scopeKey, employmentId, template, channel, year, month, reportBlocked]);

  const saveFields = async (signal?: AbortSignal): Promise<boolean> => {
    if (saveInProgress.current || !activeLinks || loadedPreferencesKey.current !== preferenceKey) return false;
    saveInProgress.current = true;
    setSavingFields(true);
    const requestContextKey = loadContextKey;
    const saveGeneration = contextGeneration.current;
    const requestChannel = channel;
    const submittedFields = [...preferences.fields];
    const submittedVersion = preferences.version;
    const query = `?channel=${requestChannel}`;
    const preferencesUrl = template === 'employee-statement' ||
      (employmentId == null && template !== 'monthly-timesheet')
      ? `/personal-report-delivery/me/fields/${template}${query}`
      : `/restaurant-personnel/report-preferences/${template}${query}`;
    try {
      const response = await api.put<Preferences>(preferencesUrl,
        { version: submittedVersion, fields: submittedFields }, { signal });
      if (signal?.aborted || currentLoadContextKey.current !== requestContextKey ||
          contextGeneration.current !== saveGeneration)
        return false;
      if (response.data.channel !== requestChannel || !Array.isArray(response.data.fields) ||
          typeof response.data.version !== 'number')
        throw new Error('Збережені поля не відповідають вибраному каналу. Оновіть сторінку.');
      setPreferences(current => ({
        ...response.data,
        fields: current.fields.length === submittedFields.length &&
          current.fields.every((field, index) => field === submittedFields[index])
          ? response.data.fields
          : current.fields,
      }));
      setRecoveryKey('');
      return true;
    } finally {
      saveInProgress.current = false;
      setSavingFields(false);
    }
  };

  const saveManually = async () => {
    if (saveInProgress.current) return;
    invalidate();
    const controller = new AbortController();
    request.current = controller;
    setError('');
    setStatus('');
    try {
      await saveFields(controller.signal);
    } catch (failure) {
      if (!controller.signal.aborted && currentLoadContextKey.current === loadContextKey)
        setError(extractApiErrorMessage(failure, 'Не вдалося зберегти поля звіту.'));
    } finally {
      if (request.current === controller) request.current = null;
    }
  };

  const createPairingCode = async () => {
    invalidate();
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const currentGeneration = ++generation.current;
    const requestIdentityContextKey = identityContextKey;
    setBusy(true);
    setError('');
    setPairingCode('');
    setStatus('');
    try {
      const response = await api.post<{ code: string }>(
        `/personal-report-delivery/links/${channel}/challenge`, { consent }, { signal: controller.signal });
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentIdentityContextKey.current !== requestIdentityContextKey) return;
      setPairingCode(response.data.code);
      setStatus(channel === 'telegram'
        ? 'Надішліть одноразовий код налаштованому боту в приватному чаті командою /start.'
        : 'Надішліть одноразовий код у приватному чаті налаштованого Viber-бота.');
    } catch (failure) {
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentIdentityContextKey.current === requestIdentityContextKey)
        setError(extractApiErrorMessage(failure, 'Не вдалося створити одноразовий код прив’язки.'));
    } finally {
      if (request.current === controller) request.current = null;
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentIdentityContextKey.current === requestIdentityContextKey) setBusy(false);
    }
  };

  const revoke = async (target: Channel) => {
    invalidate();
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const currentGeneration = ++generation.current;
    const requestContextKey = loadContextKey;
    const requestIdentityContextKey = identityContextKey;
    setBusy(true);
    setError('');
    try {
      await api.delete(`/personal-report-delivery/links/${target}`, { signal: controller.signal });
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentLoadContextKey.current !== requestContextKey ||
          currentIdentityContextKey.current !== requestIdentityContextKey) return;
      setPairingCode('');
      await load();
    } catch (failure) {
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentLoadContextKey.current === requestContextKey &&
          currentIdentityContextKey.current === requestIdentityContextKey)
        setError(extractApiErrorMessage(failure, 'Не вдалося відкликати прив’язку особистого каналу.'));
    } finally {
      if (request.current === controller) request.current = null;
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentLoadContextKey.current === requestContextKey &&
          currentIdentityContextKey.current === requestIdentityContextKey) setBusy(false);
    }
  };

  const prepare = async () => {
    if (reportBlocked || activity || recoveryKey === preferenceKey) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const currentGeneration = ++generation.current;
    const requestContextKey = contextKey;
    setBusy(true);
    setError('');
    setStatus('');
    setPreview(null);
    try {
      const fieldsSaved = await saveFields(controller.signal);
      if (!fieldsSaved) return;
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentContextKey.current !== requestContextKey) return;
      const response = await api.post<PreviewResponse>('/personal-report-delivery/preview', {
        template, channel, year, month, employmentId: employmentId ?? null,
      }, { signal: controller.signal });
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentContextKey.current !== requestContextKey) return;
      setPreview({ ...response.data, contextKey: requestContextKey, generation: currentGeneration });
    } catch (failure) {
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentContextKey.current === requestContextKey)
        setError(extractApiErrorMessage(failure, 'Не вдалося створити захищений попередній перегляд.'));
    } finally {
      if (!controller.signal.aborted && currentGeneration === generation.current) setBusy(false);
    }
  };

  const confirmDelivery = async () => {
    if (reportBlocked || activity || recoveryKey === preferenceKey || !preview || preview.contextKey !== currentContextKey.current ||
        preview.generation !== generation.current) return;
    const previewToConfirm = preview;
    const requestContextKey = currentContextKey.current;
    focusAfterConfirm.current = true;
    request.current?.abort();
    const controller = new AbortController();
    const currentGeneration = generation.current;
    setBusy(true);
    setError('');
    let delivery: symbol | undefined;
    let uncertain = true;
    let knownResult: 'accepted' | 'delivered' | 'failed' | 'rejected' | 'document-failed' = 'rejected';
    try {
      delivery = beginPersonalReportDelivery();
      const response = await api.post<{ status: string; errorCode?: string; message?: string }>(
        '/personal-report-delivery/confirm', { previewId: previewToConfirm.previewId }, { signal: controller.signal });
      uncertain = !['delivered', 'accepted', 'failed'].includes(response.data.status);
      if (response.data.status === 'accepted' || response.data.status === 'delivered' || response.data.status === 'failed')
        knownResult = response.data.status;
      if (response.data.status === 'failed' && response.data.errorCode === 'document-generation-failed')
        knownResult = 'document-failed';
      if (controller.signal.aborted || currentGeneration !== generation.current ||
          currentContextKey.current !== requestContextKey) return;
      if (response.data.status === 'delivered')
        setStatus('Провайдер підтвердив доставку на пристрій.');
      else if (response.data.status === 'accepted')
        setStatus('Провайдер прийняв запит; доставку не підтверджено. Перевірте особистий канал одержувача.');
      else if (response.data.status === 'unknown')
        setStatus(response.data.message ||
          'Результат невідомий. Не повторюйте відправку цього звіту; спершу перевірте особистий канал одержувача.');
      else if (response.data.status === 'failed')
        setStatus('Приватну відправку не виконано. Перевірте налаштування та особистий канал одержувача.');
      else
        setStatus('Приватну відправку не підтверджено. Перевірте стан каналу або створіть новий перегляд.');
      setPreview(null);
    } catch (failure) {
      const responseValue = typeof failure === 'object' && failure !== null && 'response' in failure
        ? failure.response : null;
      const response = typeof responseValue === 'object' && responseValue !== null
        ? responseValue : null;
      const responseStatus = response && 'status' in response && typeof response.status === 'number'
        ? response.status : null;
      const responseData = response && 'data' in response && typeof response.data === 'object' &&
        response.data !== null ? response.data : null;
      const concurrentDuplicate = responseData && 'status' in responseData &&
        responseData.status === 'duplicate';
      uncertain = responseStatus === null || responseStatus >= 500 || !!concurrentDuplicate;
      if (!controller.signal.aborted && currentGeneration === generation.current &&
          currentContextKey.current === requestContextKey) {
        if (!delivery) {
          setError('Не вдалося зберегти попередження про непідтверджену спробу. Відправлення не починалося.');
        } else if (uncertain) {
          setStatus('Підтвердження не отримано; звіт міг бути доставлений. Результат невідомий — не повторюйте відправку й перевірте особистий канал одержувача.');
          setPreview(null);
        } else {
          setError(extractApiErrorMessage(failure, 'Не вдалося підтвердити відправку звіту.'));
        }
      }
    } finally {
      if (delivery) finishPersonalReportDelivery(delivery, uncertain, knownResult);
      if (!controller.signal.aborted && currentGeneration === generation.current) setBusy(false);
    }
  };

  const ready = loadedPreferencesKey.current === preferenceKey && preferences.channel === channel &&
    (channel === 'telegram' ? providers.telegramReady : providers.viberReady);
  const recovering = recoveryKey === preferenceKey;
  const linked = links.some(item => item.channel === channel);
  const activePreview = preview?.contextKey === contextKey && preview.generation === generation.current
    ? preview : null;
  const deliveryStatus = activity === 'pending'
    ? 'Триває підтвердження приватної відправки. До встановлення результату не повторюйте відправку.'
    : activity === 'unknown'
      ? 'Підтвердження не отримано; звіт міг бути доставлений. Результат невідомий — не повторюйте відправку й перевірте особистий канал одержувача.'
      : activity === 'accepted'
        ? 'Провайдер прийняв запит; доставку не підтверджено. Не повторюйте відправку; перевірте особистий канал одержувача.'
        : activity === 'delivered'
          ? 'Провайдер підтвердив доставку на пристрій. Не повторюйте відправку без перевірки особистого каналу одержувача.'
          : activity === 'failed'
            ? 'Приватну відправку не виконано. Перевірте налаштування та особистий канал одержувача перед новою спробою.'
            : activity === 'document-failed'
              ? 'Не вдалося сформувати PDF-звіт. Відправлення до провайдера не починалося.'
              : activity === 'rejected'
              ? 'Сервер відхилив підтвердження приватної відправки. Перевірте стан особистого каналу перед новою спробою.'
              : status;
  const selectedFields = template === 'employee-statement' ? statementFields
    : template === 'employee-timesheet' ? personalTimesheetFields : monthlyTimesheetFields;
  const defaultFields = restaurantReportDefaultFields(template, channel) as Field[];
  const attendanceUnavailable = employmentId != null && template === 'employee-statement' &&
    !attendanceDeliveryAllowed;
  const hasUnavailableSelectedAttendance = attendanceUnavailable &&
    preferences.fields.some(field => field.startsWith('attendance'));
  const revenueUnavailable = template === 'employee-statement' &&
    preferences.fields.includes('payroll-revenue') && !revenueDeliveryAllowed;

  return <section className="personal-report-delivery" aria-label="Приватна відправка звіту">
    <h3>Приватна відправка звіту</h3>
    <p>Сервер формує звіт і показує його для перевірки. Відправка відбудеться лише після вашого підтвердження. Особисті адреси не беруться з бізнес-сповіщень.</p>
    {recovering && <p role="alert">{reportPreferenceRecoveryMessage}</p>}
    {reportBlocked && <p role="alert">Приватну відправку заблоковано до завантаження та успішного збереження придатного вибору полів у батьківській формі. Уже розпочата спроба не скасовується; перевірте її результат нижче.</p>}
    <div className="personal-report-delivery__row">
      <label>Канал
        <select value={channel} disabled={busy} onChange={event => {
          invalidate();
          setChannel(event.target.value as Channel);
          setPairingCode('');
          setConsent(false);
          setStatus('');
          setError('');
        }}>
          <option value="telegram">Telegram</option>
          <option value="viber">Viber</option>
        </select>
      </label>
      <span>{ready ? (linked ? 'Перевірена особиста прив’язка активна' : 'Провайдер готовий; потрібна прив’язка') :
        channel === 'viber'
          ? 'Недоступно до налаштування комерційного бота, токена й підписаного webhook та його підтвердження.'
          : 'Недоступно до налаштування окремого персонального бота, токена й секрету webhook та його підтвердження.'}</span>
    </div>
    {links.map(item => <div className="personal-report-delivery__link" key={item.channel}>
      <span>{employmentId == null && item.consentedAtUtc
        ? `${item.channel} прив’язано за згодою ${new Date(item.consentedAtUtc).toLocaleDateString('uk-UA')}`
        : `Особисту згоду для ${item.channel} активовано`}</span>
      {employmentId == null && <button type="button" disabled={busy} onClick={() => void revoke(item.channel)}>Відкликати</button>}
    </div>)}
    {employmentId != null && <p>Працівник самостійно прив’язує та відкликає особистий акаунт. Використовується лише його активна згода; адресу каналу не показано.</p>}
    {employmentId == null && !linked && <div className="personal-report-delivery__row">
      <label><input type="checkbox" checked={consent} disabled={busy || !ready}
        onChange={event => setConsent(event.target.checked)} />
        Погоджуюся отримувати особисті звіти про зарплату чи табелювання в цьому каналі.</label>
      <button type="button" disabled={busy || savingFields || !ready || !consent} onClick={() => void createPairingCode()}>
        Створити одноразовий код прив’язки
      </button>
    </div>}
    {pairingCode && <p className="personal-report-delivery__code" role="status">{pairingCode}</p>}
    <button type="button" disabled={busy || savingFields} onClick={() => void load()}>Оновити стан прив’язки</button>
    <fieldset disabled={busy || !activeLinks || loadedPreferencesKey.current !== preferenceKey}>
      <legend>Поля для {channel === 'telegram' ? 'Telegram' : 'Viber'} (окремо від друку)</legend>
      {selectedFields.map(([key, label]) => <label key={key}>
        <input type="checkbox" checked={preferences.fields.includes(key)}
          disabled={(attendanceUnavailable && key.startsWith('attendance') && !preferences.fields.includes(key)) ||
            (key === 'payroll-revenue' && !revenueDeliveryAllowed && !preferences.fields.includes(key))}
          onChange={event => {
            invalidate();
            setPreferences(current => ({
              ...current,
              fields: event.target.checked
                ? [...current.fields, key]
                : current.fields.filter(field => field !== key),
            }));
          }} />
        {label}
      </label>)}
      {attendanceUnavailable && <p>Надсилання табельних полів іншому працівнику потребує права адміністратора сторінки та локального права керування персоналом.</p>}
      {template === 'employee-statement' && <p>Виторг ресторану — окреме поле, доступне лише відправнику з правом адміністратора сторінки; ставки працівника від нього не залежать.</p>}
      <button type="button" disabled={busy || savingFields} onClick={() => {
        void saveManually();
      }}>
        Зберегти поля цього каналу
      </button>
      <button type="button" disabled={busy || savingFields} onClick={() => {
        invalidate();
        setPreferences(current => ({ ...current, fields: [...defaultFields] }));
      }}>Типові поля цього каналу</button>
    </fieldset>
    {template === 'monthly-timesheet' && channel === 'viber' &&
      <p>Viber не підтримує безпечне завантаження PDF. Можна надіслати лише текст у межах обмежень провайдера; публічне посилання на файл не створюється. Для повного табеля скористайтеся локальним друком/PDF.</p>}
    {template === 'monthly-timesheet' && channel === 'telegram' &&
      <p>Telegram отримає приватний PDF, створений сервером; посилання на файл не публікується.</p>}
    {template === 'employee-timesheet' && channel === 'telegram' &&
      <p>Telegram отримає приватний PDF: {employmentId == null ? 'ваш особистий табель' : 'особистий табель вибраного працівника'}; зарплатні поля та інші працівники не включаються.</p>}
    {template === 'employee-timesheet' && channel === 'viber' &&
      <p>Viber отримає лише текст з особистого табеля вибраного одержувача; зарплатні поля та інші працівники не включаються.</p>}
    <button type="button" disabled={reportBlocked || busy || savingFields || recovering || !!activity || !ready || !linked ||
      hasUnavailableSelectedAttendance || revenueUnavailable}
      onClick={() => void prepare()}>
      Створити попередній перегляд
    </button>
    {activePreview && <div className="personal-report-delivery__preview">
      <p>Перевірте цей звіт. Перегляд дійсний п’ять хвилин; зміна даних, дозволів, згоди, каналу, одержувача або збережених полів його анулює.</p>
      <p>{activePreview.deliveryFormat === 'private-pdf'
        ? 'Після підтвердження Telegram надішле цей звіт як приватний PDF-файл.'
        : 'Після підтвердження цей звіт буде надісланий як приватне текстове повідомлення.'}</p>
      <pre>{activePreview.text}</pre>
      <button type="button" disabled={busy} onClick={invalidate}>Скасувати попередній перегляд</button>
      <button type="button" disabled={reportBlocked || busy || recovering || !!activity} onClick={() => void confirmDelivery()}>
        Підтвердити приватну відправку
      </button>
    </div>}
    {error && <p ref={errorRef} role="alert" tabIndex={-1}>{error}</p>}
    {deliveryStatus && <p ref={statusRef} role="status" tabIndex={-1}>{deliveryStatus}</p>}
    {activity === 'storage-unavailable' &&
      <p role="alert">Недоступне сховище стану непідтвердженої відправки. Приватна відправка заблокована.</p>}
    {activity && activity !== 'pending' &&
      <button type="button" onClick={() => {
        try {
          acknowledgeUnknownPersonalReportDelivery();
          setStatus('');
        } catch {
          setError('Не вдалося оновити стан непідтвердженої спроби. Відправка залишається заблокованою.');
        }
      }}>Канал перевірено; дозволити новий перегляд</button>}
  </section>;
};

export default PersonalReportDeliveryPanel;
